import {PaymentMethod as TPaymentMethod,
	ExpressButtonsConfig,
	MessageConfigurationsConfig,
} from 'coreStore/data/basket/types';

/**
 * Represents PaymentMethod
 */
class PaymentMethod {
	private ID: string
	private name: string
	private paymentProcessorID: string
	private publicConfigurations: Object | null
	private applicableForBasket: boolean
	private excludedProducts: Array<string>
	private expressButtons: ExpressButtonsConfig;
	private messageConfigurations: MessageConfigurationsConfig;

	/**
	 * @constructor
	 * @param {TPaymentMethod} paymentMethodObject - object with payment method data
	 */
	constructor(paymentMethodObject: TPaymentMethod) {
		this.ID = paymentMethodObject.id;
		this.name = paymentMethodObject.name || '';
		this.paymentProcessorID = paymentMethodObject.paymentProcessorId || '';
		this.publicConfigurations = paymentMethodObject.c_publicConfigurations || null;
		this.applicableForBasket = paymentMethodObject.c_applicableForBasket;
		this.excludedProducts = paymentMethodObject.c_excludedProducts;
		this.expressButtons = paymentMethodObject.c_expressButtons;
		this.messageConfigurations = paymentMethodObject.c_messageConfigurations;
	}

	/**
	 * Returns express buttons
	 * @returns {ExpressButtonsConfig}
	 */
	public getExpressButtons(): ExpressButtonsConfig {
		return this.expressButtons;
	}

	/**
	 * Returns message configurations
	 * @returns {MessageConfigurationsConfig}
	 */
	public getMessageConfigurations(): MessageConfigurationsConfig {
		return this.messageConfigurations;
	}

	/**
	 * Returns payment method ID
	 * @returns {string} - payment method ID
	 */
	public getID(): string {
		return this.ID;
	}

	/**
	 * Returns the name of the payment method
	 * @returns {string} - name of the payment method
	 */
	public getName(): string {
		return this.name;
	}

	/**
	 * Returns payment processor ID
	 * @returns {string} - payment processor ID
	 */
	public getPaymentProcessorID(): string {
		return this.paymentProcessorID;
	}

	/**
	 * Returns public configurations for payment method used for processing payment
	 * @returns {Object|null} - public configurations
	 */
	public getPublicConfigurations(): any {
		return this.publicConfigurations;
	};

	/**
	 * Returns true if payment method is applicable
	 * @param {string} [productID] - product ID to check applicability
	 * @returns {boolean} - applicability result
	 */
	public isApplicable(productID?: string): boolean {
		return productID ? this.isApplicableForProduct(productID) : this.isApplicableForBasket();
	}

	/**
	 * Indicates whatever payment method is applicable for the current basket
	 * @returns {boolean} - is payment method is applicable for the current basket
	 */
	protected isApplicableForBasket(): boolean {
		return this.applicableForBasket;
	}

	/**
	 * Indicates whatever payment method is applicable for product
	 * @param {string} productID - product ID to check applicability
	 * @returns {string} - payment method is applicable for product
	 */
	protected isApplicableForProduct(productID: string): boolean {
		return !this.excludedProducts.includes(productID);
	}
}

export default PaymentMethod;
